// context/SubscriptionContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';
import { useAuth } from './AuthContext';

// Create the context
const SubscriptionContext = createContext();

// Create a provider component
export const SubscriptionProvider = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [creditBalance, setCreditBalance] = useState(0);
  const [creditTransactions, setCreditTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch subscription plans
  const fetchSubscriptionPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const { plans } = await apiService.getSubscriptionPlans();
      setSubscriptionPlans(plans);
    } catch (err) {
      console.error('Error fetching subscription plans:', err);
      setError(t('subscription.errorFetchingPlans'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch user subscriptions
  const fetchUserSubscriptions = async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoading(true);
      setError(null);
      const { subscriptions } = await apiService.getUserSubscriptions();
      
      // The backend now handles deduplication by plan name,
      // but we'll keep this as a safety measure
      const planNameMap = new Map();
      
      // Group subscriptions by plan_name and keep the most recent one
      subscriptions.forEach(sub => {
        if (!planNameMap.has(sub.plan_name) || 
            new Date(sub.created_at) > new Date(planNameMap.get(sub.plan_name).created_at)) {
          planNameMap.set(sub.plan_name, sub);
        }
      });
      
      // Convert map values back to array
      setUserSubscriptions([...planNameMap.values()]);
      
      // Find the active subscription
      const active = subscriptions.find(sub => 
        sub.status === 'active' && new Date(sub.end_date) > new Date()
      );
      setActiveSubscription(active || null);
    } catch (err) {
      console.error('Error fetching user subscriptions:', err);
      setError(t('subscription.errorFetchingSubscriptions'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch credit balance
  const fetchCreditBalance = async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoading(true);
      setError(null);
      const { balance } = await apiService.getCreditBalance();
      setCreditBalance(balance);
    } catch (err) {
      console.error('Error fetching credit balance:', err);
      setError(t('subscription.errorFetchingBalance'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch credit transactions
  const fetchCreditTransactions = async (limit = 10, offset = 0) => {
    if (!isAuthenticated) return;
    
    try {
      setLoading(true);
      setError(null);
      const { transactions } = await apiService.getCreditTransactions(limit, offset);
      setCreditTransactions(transactions);
    } catch (err) {
      console.error('Error fetching credit transactions:', err);
      setError(t('subscription.errorFetchingTransactions'));
    } finally {
      setLoading(false);
    }
  };

  // Subscribe to a plan
  const subscribeToPlan = async (planId, paymentMethodId, autoRenew = true) => {
    try {
      setLoading(true);
      setError(null);
      
      // First, check if there are any active subscriptions that need to be cancelled
      if (userSubscriptions && userSubscriptions.length > 0) {
        const activeSubscriptions = userSubscriptions.filter(sub => 
          sub.status === 'active' && new Date(sub.end_date) > new Date()
        );
        
        // Cancel all active subscriptions
        for (const sub of activeSubscriptions) {
          try {
            await apiService.cancelSubscription(sub.id);
            console.log(`Automatically cancelled subscription ${sub.id} for plan ${sub.plan_name}`);
          } catch (cancelErr) {
            console.error(`Error cancelling subscription ${sub.id}:`, cancelErr);
            // Continue with the new subscription even if cancellation fails
          }
        }
      }
      
      // Now subscribe to the new plan
      const result = await apiService.subscribeToPlan(planId, paymentMethodId, autoRenew);
      
      // Check if we received a checkout URL from Stripe
      if (result.checkoutUrl) {
        // Redirect to the Stripe checkout page
        window.location.href = result.checkoutUrl;
        return result;
      }
      
      // If no checkout URL, proceed with the normal flow
      // Refresh user subscriptions and credit balance
      await fetchUserSubscriptions();
      await fetchCreditBalance();
      
      return result;
    } catch (err) {
      console.error('Error subscribing to plan:', err);
      
      // Check if this is a duplicate subscription error
      if (err.message && err.message.includes('already have an active')) {
        setError(err.message);
      } else {
        setError(t('subscription.errorSubscribing'));
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Cancel a subscription
  const cancelSubscription = async (subscriptionId) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.cancelSubscription(subscriptionId);
      
      // Refresh user subscriptions
      await fetchUserSubscriptions();
      
      return result;
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError(t('subscription.errorCancelling'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Renew a subscription
  const renewSubscription = async (subscriptionId, paymentMethodId) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.renewSubscription(subscriptionId, paymentMethodId);
      
      // Refresh user subscriptions and credit balance
      await fetchUserSubscriptions();
      await fetchCreditBalance();
      
      return result;
    } catch (err) {
      console.error('Error renewing subscription:', err);
      setError(t('subscription.errorRenewing'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Purchase credits
  const purchaseCredits = async (amount, paymentMethodId) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.purchaseCredits(amount, paymentMethodId);
      
      // Refresh credit balance and transactions
      await fetchCreditBalance();
      await fetchCreditTransactions();
      
      return result;
    } catch (err) {
      console.error('Error purchasing credits:', err);
      setError(t('subscription.errorPurchasingCredits'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Refresh subscription data
  const refreshSubscription = async () => {
    // Prevent multiple simultaneous calls
    if (isRefreshing) {
      console.log('Subscription refresh already in progress, skipping duplicate call');
      return activeSubscription;
    }
    
    try {
      setIsRefreshing(true);
      await fetchUserSubscriptions();
      return activeSubscription;
    } finally {
      // Set a small delay before allowing another refresh
      setTimeout(() => {
        setIsRefreshing(false);
      }, 1000);
    }
  };

  // Refresh credit balance
  const refreshCreditBalance = async () => {
    await fetchCreditBalance();
    return creditBalance;
  };

  // Refresh credit transactions
  const refreshCreditTransactions = async () => {
    await fetchCreditTransactions();
    return creditTransactions;
  };

  // Effect to fetch data when the authentication state changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscriptionPlans();
      fetchUserSubscriptions();
      fetchCreditBalance();
      fetchCreditTransactions();
    } else {
      // Reset state when not authenticated
      setUserSubscriptions([]);
      setActiveSubscription(null);
      setCreditBalance(0);
      setCreditTransactions([]);
    }
  }, [isAuthenticated, user?.id]);

  // Provide the context value
  const value = {
    subscriptionPlans,
    userSubscriptions,
    activeSubscription,
    creditBalance,
    creditTransactions,
    loading,
    error,
    fetchSubscriptionPlans,
    fetchUserSubscriptions,
    fetchCreditBalance,
    fetchCreditTransactions,
    subscribeToPlan,
    cancelSubscription,
    renewSubscription,
    purchaseCredits,
    refreshSubscription,
    refreshCreditBalance,
    refreshCreditTransactions
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// Custom hook to use the subscription context
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
